import { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import Template from '../Template/Template';
import ShowMessage from 'components/ShowMessage/ShowMessage';
import FeedbackLoading from '../feedback-loading/FeedbackLoading';
import { feedbackService } from 'modules/Feedback/services/feedback';
import aspireLogo from '../../../../assets/img/aspire-logo.png';
import './FeedbackResponse.scss';

const FeedbackResponseContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const feedbackToken = useMemo(
    () => new URLSearchParams(location.search).get('token'),
    [location.search]
  );
  const [template, setTemplate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const idToken = localStorage.getItem('idToken');
  const decodeToken = idToken && idToken !== 'undefined' ? jwt.decode(idToken) : null;
  const loginPath = feedbackToken
    ? `/feedback/login?token=${encodeURIComponent(feedbackToken)}`
    : '/feedback/login';

  useEffect(() => {
    if (alreadySubmitted) return;

    if (!feedbackToken) {
      setLoading(false);
      setError('This feedback link is missing a token.');
      return;
    }

    if (!idToken || idToken === 'undefined') {
      history.replace(loginPath);
      return;
    }

    if (String(decodeToken?.feedbackToken) !== String(feedbackToken)) {
      localStorage.removeItem('idToken');
      history.replace(loginPath);
      return;
    }

    const getTemplate = async () => {
      try {
        const feedback = await feedbackService.getFeedbackByToken(feedbackToken);
        const nextTemplate = {
          name: feedback.data.templateName,
          ...feedback.data
        };
        setTemplate(nextTemplate);
        if (nextTemplate.status === 'completed') {
          setAlreadySubmitted(true);
          localStorage.removeItem('idToken');
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem('idToken');
          history.replace(loginPath);
          return;
        }
        if (err?.response?.status === 403) {
          setAlreadySubmitted(true);
          localStorage.removeItem('idToken');
          return;
        }
        setError('Failed to load feedback template.');
      } finally {
        setLoading(false);
      }
    };

    getTemplate();
  }, [feedbackToken, idToken, decodeToken?.feedbackToken, history, alreadySubmitted, loginPath]);

  if (!feedbackToken) {
    return (
      <ShowMessage>
        <h1>Invalid feedback link</h1>
        <p>
          This feedback link is missing a token. Please use the link from your invitation email.
        </p>
      </ShowMessage>
    );
  }

  if (alreadySubmitted) {
    return (
      <ShowMessage>
        <h1>Feedback already submitted</h1>
        <p>Thanks — this feedback was already received. No further action is needed.</p>
      </ShowMessage>
    );
  }

  if (!idToken || idToken === 'undefined') {
    return <FeedbackLoading message="Checking your access…" />;
  }

  if (loading) {
    return <FeedbackLoading message="Loading your feedback…" />;
  }

  if (error) {
    return (
      <ShowMessage>
        <h1>Sorry!</h1>
        <p>{error}</p>
      </ShowMessage>
    );
  }

  if (String(decodeToken?.feedbackToken) !== String(feedbackToken)) {
    return (
      <ShowMessage>
        <h1>Sorry!</h1>
        <p>You do not have access to this feedback</p>
      </ShowMessage>
    );
  }

  return (
    <div className="feedbackResponsePage">
      <div className="feedbackResponseBackdrop" aria-hidden="true" />
      <header className="feedbackResponseHeader">
        <div className="feedbackResponseHeaderInner">
          <img src={aspireLogo} alt="Aspire" className="feedbackResponseBrand" />
          <div className="feedbackResponseHeaderMeta">
            <span className="feedbackResponseLabel">Feedback for</span>
            <span className="feedbackResponseSubject">
              {template?.employeeName || 'team member'}
            </span>
          </div>
        </div>
      </header>
      <Template feedbackToken={feedbackToken} template={template} hidePageTitle />
    </div>
  );
};

export default FeedbackResponseContainer;
